import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal, Table, Button, Space, Tag, Form, Input, Select, DatePicker, InputNumber,
  Popconfirm, message, Alert, Typography, Divider, Tooltip,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, LinkOutlined, DisconnectOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { unifiedShipmentsAPI, SupplierInvoice, SupplierInvoicePayload } from '../services/api.ts';
import { documentTypeLabel, documentTypeColor } from '../utils/documentTypes.ts';

const { Text } = Typography;

/**
 * Fatture e packing list del fornitore di una spedizione (v3.8.0).
 * Dati di riferimento: non muovono merce, si gestiscono in qualunque stato
 * della spedizione. Ogni riga di spedizione può essere collegata a una fattura
 * del suo stesso fornitore; il legame con le dichiarazioni H2/H1 e i DDT si
 * vede dalle righe entrate nei documenti di carico.
 */
interface Props {
  visible: boolean;
  shipment: { id: number; shipment_code: string; supplier_id?: number | null; supplier_name?: string | null } | null;
  canEdit: boolean;
  onClose: (changed: boolean) => void;
}

interface ShipmentRow {
  id: number;
  product_code: string | null;
  product_description: string | null;
  supplier_order_number: string | null;
  supplier_id: number | null;
  supplier_name: string | null;
  quantity: number;
  supplier_invoice_id: number | null;
  invoice_number: string | null;
}

const SupplierInvoicesModal: React.FC<Props> = ({ visible, shipment, canEdit, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState<SupplierInvoice[]>([]);
  const [rows, setRows] = useState<ShipmentRow[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [targetInvoiceId, setTargetInvoiceId] = useState<number | undefined>();
  const [formVisible, setFormVisible] = useState(false);
  const [editing, setEditing] = useState<SupplierInvoice | null>(null);
  const [saving, setSaving] = useState(false);
  const [changed, setChanged] = useState(false);
  const [form] = Form.useForm();

  const load = async () => {
    if (!shipment) return;
    setLoading(true);
    try {
      const [inv, pl] = await Promise.all([
        unifiedShipmentsAPI.getInvoices(shipment.id),
        unifiedShipmentsAPI.getPackingList(shipment.id),
      ]);
      setInvoices(inv.invoices || []);
      setRows(pl.items || []);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Errore nel caricamento delle fatture');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      setSelectedRowKeys([]);
      setTargetInvoiceId(undefined);
      setChanged(false);
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, shipment?.id]);

  // Fornitori della spedizione: righe + testata
  const suppliers = useMemo(() => {
    const map = new Map<number, string>();
    rows.forEach((r) => { if (r.supplier_id) map.set(r.supplier_id, r.supplier_name || `#${r.supplier_id}`); });
    if (shipment?.supplier_id && !map.has(shipment.supplier_id)) {
      map.set(shipment.supplier_id, shipment.supplier_name || `#${shipment.supplier_id}`);
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [rows, shipment]);

  const selectedRows = rows.filter((r) => selectedRowKeys.includes(r.id));
  const selectedSuppliers = new Set(selectedRows.map((r) => r.supplier_id).filter(Boolean));
  const assignableInvoices = invoices.filter((i) => selectedSuppliers.size === 0 || (selectedSuppliers.size === 1 && selectedSuppliers.has(i.supplier_id)));

  const openForm = (inv: SupplierInvoice | null) => {
    setEditing(inv);
    setFormVisible(true);
  };

  // Valori del form impostati dopo l'apertura (il Form interno è montato solo a finestra aperta)
  useEffect(() => {
    if (!formVisible) return;
    form.resetFields();
    if (editing) {
      form.setFieldsValue({
        ...editing,
        invoice_date: editing.invoice_date ? dayjs(editing.invoice_date) : null,
        packing_list_date: editing.packing_list_date ? dayjs(editing.packing_list_date) : null,
      });
    } else {
      form.setFieldsValue({ supplier_id: suppliers.length === 1 ? suppliers[0].id : undefined });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formVisible, editing]);

  const handleSave = async () => {
    if (!shipment) return;
    let values: any;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const payload: SupplierInvoicePayload = {
      supplier_id: values.supplier_id,
      invoice_number: values.invoice_number,
      invoice_date: values.invoice_date ? values.invoice_date.format('YYYY-MM-DD') : null,
      packing_list_number: values.packing_list_number || null,
      packing_list_date: values.packing_list_date ? values.packing_list_date.format('YYYY-MM-DD') : null,
      currency: values.currency || null,
      total_amount: values.total_amount ?? null,
      notes: values.notes || null,
    };
    if (!editing) {
      // in creazione collega subito le righe libere di quel fornitore
      payload.item_ids = rows.filter((r) => !r.supplier_invoice_id && r.supplier_id === values.supplier_id).map((r) => r.id);
    }
    setSaving(true);
    try {
      const res = editing
        ? await unifiedShipmentsAPI.updateInvoice(shipment.id, editing.id, payload)
        : await unifiedShipmentsAPI.createInvoice(shipment.id, payload);
      message.success(res.message);
      setFormVisible(false);
      setChanged(true);
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Errore nel salvataggio della fattura');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (inv: SupplierInvoice) => {
    if (!shipment) return;
    try {
      const res = await unifiedShipmentsAPI.deleteInvoice(shipment.id, inv.id);
      message.success(res.message);
      setChanged(true);
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Errore nell\'eliminazione della fattura');
    }
  };

  const handleAssign = async (invoiceId: number | null) => {
    if (!shipment || selectedRowKeys.length === 0) return;
    try {
      const res = await unifiedShipmentsAPI.assignInvoice(shipment.id, invoiceId, selectedRowKeys as number[]);
      message.success(res.message);
      setSelectedRowKeys([]);
      setChanged(true);
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Errore nel collegamento delle righe');
    }
  };

  const invoiceColumns = [
    { title: 'Fornitore', dataIndex: 'supplier_name', key: 'supplier_name' },
    {
      title: 'Fattura', key: 'invoice',
      render: (_: any, r: SupplierInvoice) => (
        <span><b>{r.invoice_number}</b>{r.invoice_date ? ` del ${dayjs(r.invoice_date).format('DD/MM/YYYY')}` : ''}</span>
      ),
    },
    {
      title: 'Packing list', key: 'pl',
      render: (_: any, r: SupplierInvoice) => r.packing_list_number
        ? `${r.packing_list_number}${r.packing_list_date ? ' del ' + dayjs(r.packing_list_date).format('DD/MM/YYYY') : ''}`
        : '-',
    },
    {
      title: 'Totale', key: 'total', align: 'right' as const,
      render: (_: any, r: SupplierInvoice) => r.total_amount != null
        ? `${r.total_amount.toLocaleString('it-IT', { minimumFractionDigits: 2 })} ${r.currency || ''}`
        : '-',
    },
    { title: 'Righe / Pz', key: 'rows', align: 'right' as const, render: (_: any, r: SupplierInvoice) => `${r.items_count} / ${r.total_quantity.toLocaleString('it-IT')}` },
    {
      title: 'Documenti di carico', key: 'docs',
      render: (_: any, r: SupplierInvoice) => r.documents.length
        ? <Space size={2} wrap>{r.documents.map((d) => (
          <Tooltip key={d.id} title={`${d.quantity} pz - ${d.customs_status === 'draft' ? 'bozza' : 'elaborato'}`}>
            <Tag color={documentTypeColor(d.document_type)}>{documentTypeLabel(d.document_type)} {d.document_number}</Tag>
          </Tooltip>
        ))}</Space>
        : <Text type="secondary">-</Text>,
    },
    ...(canEdit ? [{
      title: '', key: 'actions', width: 90,
      render: (_: any, r: SupplierInvoice) => (
        <Space size={0}>
          <Button type="text" icon={<EditOutlined />} onClick={() => openForm(r)} />
          <Popconfirm title={`Eliminare la fattura ${r.invoice_number}?`} onConfirm={() => handleDelete(r)} okText="Sì" cancelText="No">
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ];

  const rowColumns = [
    { title: 'Prodotto', key: 'product', render: (_: any, r: ShipmentRow) => <span><b>{r.product_code}</b> {r.product_description}</span> },
    { title: 'Ordine fornitore', dataIndex: 'supplier_order_number', key: 'so', render: (v: string) => v || '-' },
    { title: 'Fornitore', dataIndex: 'supplier_name', key: 'supplier', render: (v: string) => v || '-' },
    { title: 'Q.tà', dataIndex: 'quantity', key: 'qty', align: 'right' as const, render: (v: number) => v?.toLocaleString('it-IT') },
    {
      title: 'Fattura', dataIndex: 'invoice_number', key: 'inv',
      render: (v: string) => v ? <Tag color="blue">{v}</Tag> : <Text type="secondary">nessuna</Text>,
    },
  ];

  const unassigned = rows.filter((r) => !r.supplier_invoice_id).length;

  return (
    <Modal
      open={visible}
      title={`Fatture e packing list del fornitore - Spedizione ${shipment?.shipment_code || ''}`}
      width={1100}
      onCancel={() => onClose(changed)}
      footer={[<Button key="close" onClick={() => onClose(changed)}>Chiudi</Button>]}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Registra le fatture del fornitore arrivate con la spedizione e collega a ciascuna le sue righe. Il dato è facoltativo e serve a ritrovare, per ogni prodotto, fattura, dichiarazione H2/H1 o DDT con cui è entrato."
      />
      <Space style={{ marginBottom: 8 }}>
        {canEdit && <Button type="primary" icon={<PlusOutlined />} onClick={() => openForm(null)} disabled={suppliers.length === 0}>Nuova fattura</Button>}
      </Space>
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={invoices}
        columns={invoiceColumns}
        pagination={false}
        locale={{ emptyText: 'Nessuna fattura registrata' }}
      />

      <Divider orientation="left">Righe della spedizione {unassigned > 0 && <Tag color="orange">{unassigned} senza fattura</Tag>}</Divider>
      {canEdit && (
        <Space style={{ marginBottom: 8 }} wrap>
          <Select
            style={{ width: 320 }}
            placeholder={selectedSuppliers.size > 1 ? 'Righe di fornitori diversi selezionate' : 'Fattura a cui collegare le righe selezionate'}
            value={targetInvoiceId}
            onChange={setTargetInvoiceId}
            disabled={selectedRowKeys.length === 0 || selectedSuppliers.size > 1}
            options={assignableInvoices.map((i) => ({ value: i.id, label: `${i.invoice_number} - ${i.supplier_name || ''}` }))}
          />
          <Button icon={<LinkOutlined />} disabled={!targetInvoiceId || selectedRowKeys.length === 0 || selectedSuppliers.size > 1} onClick={() => handleAssign(targetInvoiceId!)}>
            Collega ({selectedRowKeys.length})
          </Button>
          <Button icon={<DisconnectOutlined />} disabled={selectedRowKeys.length === 0} onClick={() => handleAssign(null)}>
            Scollega
          </Button>
        </Space>
      )}
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={rows}
        columns={rowColumns}
        pagination={{ pageSize: 50, hideOnSinglePage: true }}
        rowSelection={canEdit ? { selectedRowKeys, onChange: (keys) => { setSelectedRowKeys(keys); setTargetInvoiceId(undefined); } } : undefined}
      />

      <Modal
        open={formVisible}
        title={editing ? `Modifica fattura ${editing.invoice_number}` : 'Nuova fattura del fornitore'}
        onCancel={() => setFormVisible(false)}
        onOk={handleSave}
        okText="Salva"
        cancelText="Annulla"
        confirmLoading={saving}
        destroyOnClose
      >
        {!editing && (
          <Alert type="info" showIcon style={{ marginBottom: 12 }} message="Alla creazione le righe del fornitore ancora senza fattura vengono collegate a questa fattura: puoi poi spostarle o scollegarle." />
        )}
        <Form form={form} layout="vertical">
          <Form.Item name="supplier_id" label="Fornitore" rules={[{ required: true, message: 'Scegli il fornitore' }]}>
            <Select options={suppliers.map((s) => ({ value: s.id, label: s.name }))} />
          </Form.Item>
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="invoice_number" label="Numero fattura" rules={[{ required: true, whitespace: true, message: 'Inserisci il numero della fattura' }, { max: 100 }]}>
              <Input />
            </Form.Item>
            <Form.Item name="invoice_date" label="Data fattura">
              <DatePicker format="DD/MM/YYYY" />
            </Form.Item>
          </Space>
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="packing_list_number" label="Numero packing list" rules={[{ max: 100 }]}>
              <Input />
            </Form.Item>
            <Form.Item name="packing_list_date" label="Data packing list">
              <DatePicker format="DD/MM/YYYY" />
            </Form.Item>
          </Space>
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="total_amount" label="Totale fattura">
              <InputNumber min={0} precision={2} style={{ width: 160 }} decimalSeparator="," />
            </Form.Item>
            <Form.Item name="currency" label="Valuta" rules={[{ max: 3 }]}>
              <Input placeholder="USD" style={{ width: 90 }} maxLength={3} />
            </Form.Item>
          </Space>
          <Form.Item name="notes" label="Note">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </Modal>
  );
};

export default SupplierInvoicesModal;
